define('presentation/signup/viewModels/signupEmailViewModel',[
    'businessServices/signup/signupEntryGate',
    'common/converters/phoneNumberFormatter',
    'common/storage/workSessionIdStorage',
    'common/url/urlFormatter',
    'constants/signupSessionFlowConstants',
    'presentation/signup/common/signupNavigationManager',
    'presentation/signup/facades/signupEmailFacade',
    'presentation/signup/validators/signupEmailValidator',
    'settings/navigationConfiguration'
], function() {

    return function() {
        const self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const _i18n = require('i18next');

        const _signupSessionFlowConstants = require('constants/signupSessionFlowConstants');
        const _workSessionIdStorage = require('common/storage/workSessionIdStorage');

        const PhoneNumberFormatter = require('common/converters/phoneNumberFormatter');
        const _phoneNumberFormatter = new PhoneNumberFormatter();

        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _urlFormatter = require('common/url/urlFormatter');

        const _isSignupForCustomer = _workSessionIdStorage.getWorkSessionId() !== null && _workSessionIdStorage.getWorkSessionId() !== undefined;

        const SUCCESSFULLY_RESERVED_STATUS = "reserved";
        const EMAIL_ALREADY_IN_USE_I18N_KEY = "emailAddressUnique";

        let _facade = null;
        let _validator = null;
        let _signupEntryGate = null;
        let _signupNavigationManager = null;
        let _disposables = [];

        let _savedUserEmail = null;
        let _isEmailStepCompleted = false;

        const _numberPickerConstants = require('constants/numberPickerConstants');

        const _reservePhoneNumber = (phoneNumberObject) => {
            const phoneNumber = _phoneNumberFormatter.toEOneSixFour(phoneNumberObject.phoneNumber);

            self.isModalActive(true);
            self.isModalVisible(true);
            self.isNumberSpinnerVisible(true);
            self.phoneNumberToReserve(phoneNumber);

            const numberType = _phoneNumberFormatter.isTollFree(phoneNumber) ? _numberPickerConstants.numberTypes.tollFree : _numberPickerConstants.numberTypes.local;
            const region = phoneNumberObject.region ? phoneNumberObject.region : null;
            const city = phoneNumberObject.city ? phoneNumberObject.city : null;
            const vanityString = phoneNumberObject.vanityString ? phoneNumberObject.vanityString : null;
            const searchType = _numberPickerConstants.searchTypeEnumeration[phoneNumberObject.searchType] ?
                _numberPickerConstants.searchTypeEnumeration[phoneNumberObject.searchType] :
                null;

            _facade.reservePhoneNumber({phoneNumber, region, city, numberType, searchType, vanityString})
                .fail(_handleReservationFailure)
                .done((response) => {
                    if (response.status !== SUCCESSFULLY_RESERVED_STATUS) {
                        _handleReservationFailure();
                    } else {
                        _facade.setPhoneNumberCompleted(true)
                            .fail(_handleReservationFailure)
                            .done(() => {
                                _handleReservationSuccess(phoneNumber);
                            });
                    }
                });
        };

        const _handleReservationFailure = () => {
            self.isNumberSpinnerVisible(false);
        };

        const _handleReservationSuccess = (phoneNumber) => {
            self.isModalVisible(false);
            self.reservedPhoneNumber = phoneNumber;
            setTimeout(() => {
                self.isModalActive(false);
            }, 2500);
        };

        const _saveEmailAddress = (userEmail) => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.saveEmailAddress(userEmail)
                    .fail()
                    .done(() => {
                        if (userEmail !== _savedUserEmail) {
                            _isEmailStepCompleted = false;
                        }
                        _savedUserEmail = userEmail;

                        _signupEntryGate.evaluate(userEmail)
                            .done((verdict) => {
                                if (verdict.isAllowed) {
                                    deferredObject.resolve(true);
                                    return;
                                }

                                _signupNavigationManager.navigateToErrorPage();
                                deferredObject.resolve(false);
                            });
                    });
            });
        };

        const _handleEmailSubmit = () => {
            return _promiseFactory.defer((deferredObject) => {
                _validator.validate()
                    .fail(deferredObject.reject)
                    .done((isValid) => {
                        self.showValidation(true);

                        if (!isValid) {
                            deferredObject.resolve(false);
                            return;
                        }

                        const userEmail = self.userEmail();
                        _saveEmailAddress(userEmail)
                            .fail(deferredObject.reject)
                            .done((isSuccess) => {
                                if (!isSuccess) {
                                    deferredObject.resolve(false);
                                    return;
                                }

                                if (_isEmailStepCompleted) {
                                    deferredObject.resolve(true);
                                    return;
                                }

                                _facade.sendEmailVerificationCode()
                                    .fail(deferredObject.reject)
                                    .done(() => {
                                        deferredObject.resolve(true);
                                    });
                            });
                    });
            });
        };

        const _handleSignupForCustomer = () => {
            return _promiseFactory.defer((deferredObject) => {
                _validator.validate()
                    .fail(deferredObject.reject)
                    .done((isValid) => {
                        self.showValidation(true);

                        if (!isValid) {
                            deferredObject.resolve(false);
                            return;
                        }

                        const userEmail = self.userEmail();
                        const userVerificationCode = 0;

                        _saveEmailAddress(userEmail)
                            .fail(deferredObject.reject)
                            .done((isSuccess) => {
                                if (!isSuccess) {
                                    deferredObject.resolve(false);
                                    return;
                                }

                                _facade.setEmailStepCompleted(userEmail, userVerificationCode)
                                    .done((result) => {
                                        deferredObject.resolve(true);
                                    });
                            });
                    });

            });
        };

        const _prePopulateInputValues = (signupEmailPresentationObject) => {
            const { emailAddress, isEmailStepCompleted, reservedPhoneNumber } = signupEmailPresentationObject;
            if (emailAddress) {
                self.userEmail(emailAddress);
                _savedUserEmail = emailAddress;
                _isEmailStepCompleted = !!isEmailStepCompleted;
                self.isEmailArrowValid(true);
            }
            self.reservedPhoneNumber = reservedPhoneNumber;
        };

        self.flowId = ko.observable();
        self.userEmail = ko.observable('').extend({ rateLimit: 500 });
        self.isSignupForCustomer = ko.observable(_isSignupForCustomer);
        self.isEmailArrowValid = ko.observable(false);
        self.isCompositionComplete = ko.observable(false);
        self.showValidation = ko.observable(false);

        self.showEmailValidation = ko.pureComputed(() => {
            if (self.showValidation()) {
                return true;
            }

            if (!ko.isObservable(self.userEmail.isValid)) {
                return false;
            }

            return self.userEmail.isValid() === false &&
                self.userEmail.validationMessage() === _i18n.t(EMAIL_ALREADY_IN_USE_I18N_KEY);
        });

        self.isNavigateToNextPageActive = ko.observable(true);
        self.isModalActive = ko.observable(false);
        self.isModalVisible = ko.observable(false);
        self.isNumberSpinnerVisible = ko.observable(false);
        self.phoneNumberToReserve = ko.observable("");

        self.headingText = ko.observable(_i18n.t('signupEmail:headingText'));
        self.subtitleText = ko.observable(_i18n.t('signupEmail:subtitleText'));

        self.modalPhoneNumber = ko.pureComputed(() => {
            return _phoneNumberFormatter.toUSAreaCode(self.phoneNumberToReserve());
        });
        self.modalHeaderKey = ko.pureComputed(() => {
            return self.isNumberSpinnerVisible() ? "reservationInProgressHeader" : "reservationFailedHeader";
        });
        self.modalMessageKey = ko.pureComputed(() => {
            return self.isNumberSpinnerVisible() ? "reservationInProgressMessage" : "reservationFailedMessage";
        });
        self.reservedPhoneNumber = "";
        self.homeUrl = _urlFormatter.buildFrontendUrl("/");

        self.showNavigateToPreviousPage = ko.observable(false);

        self.isArrowValid = ko.pureComputed(() => self.isEmailArrowValid());

        self.navigateToFlowB = () => {
            const isTollFree = _phoneNumberFormatter.isTollFree(self.phoneNumberToReserve());
            if (isTollFree) {
                _signupNavigationManager.navigateToRoute(_navigationConfiguration.routesById.signupPhoneNumberTollfree.routeId, _signupSessionFlowConstants.flowB);
            } else {
                _signupNavigationManager.navigateToRoute(_navigationConfiguration.routesById.signupPhoneNumberLocal.routeId, _signupSessionFlowConstants.flowB);
            }
        };

        self.onForwardArrowClicked = () => {
            if (_isSignupForCustomer) {
                return _handleSignupForCustomer();
            }

            return _handleEmailSubmit();
        };

        self.validate = () => {
            return _promiseFactory.defer((deferredObject) => {
                _validator.validate()
                    .done((isValid) => {
                        self.showValidation(true);

                        const accountInfo = {
                            emailAddress: self.userEmail()
                        };

                        deferredObject.resolve({isValid: isValid, accountInfo: accountInfo});
                    })
                    .fail(deferredObject.reject);
            });
        };

        self.detached = () => {
            _signupNavigationManager.detached();
            _disposeSubscriptions();
        };

        const _disposeSubscriptions = () => {
            _disposables.forEach(subscription => subscription.dispose());
            _disposables = [];
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.activate = (flow = null, phoneNumberToReserve = null) => {
            const FacadeConstructor = require('presentation/signup/facades/signupEmailFacade');
            _facade = new FacadeConstructor();
            _facade.init();

            const ValidatorConstructor = require('presentation/signup/validators/signupEmailValidator');
            _validator = new ValidatorConstructor();

            const SignupEntryGateConstructor = require('businessServices/signup/signupEntryGate');
            _signupEntryGate = new SignupEntryGateConstructor();
            _signupEntryGate.init();

            const SignupNavigationManagerConstructor = require('presentation/signup/common/signupNavigationManager');
            _signupNavigationManager = new SignupNavigationManagerConstructor();
            _signupNavigationManager.init();

            self.flowId(_signupNavigationManager.getFlowId());

            return _initialize(phoneNumberToReserve);
        };

        const _initialize = (phoneNumberToReserve) => {
            _promiseFactory.deferIndefinitely(promise => {
                _facade.loadSignupSession()
                    .fail(promise.reject)
                    .done(signupEmailPresentationObject => {
                        _prePopulateInputValues(signupEmailPresentationObject);
                        promise.resolve();
                    });
            });

            _promiseFactory.defer((beginEntryGatePromise) => {
                _signupEntryGate.begin()
                    .fail(beginEntryGatePromise.resolve)
                    .done(beginEntryGatePromise.resolve);
            });

            _validator.registerViewModel(self, _facade);

            if (phoneNumberToReserve !== null &&
                phoneNumberToReserve.phoneNumber !== undefined &&
                self.flowId() === _signupSessionFlowConstants.flowC &&
                !self.reservedPhoneNumber
            ) {
                _reservePhoneNumber(phoneNumberToReserve);
            }

            return _promiseFactory.wait();
        };
    };
});

